/**
 * DashboardHeader Component
 * 
 * Top navigation bar for the dashboard with logo, title, user info, and notifications.
 * 
 * Features:
 * - BizHub logo display
 * - Page title ("Tax Dashboard")
 * - User avatar and name (from TierContext)
 * - Tier badge (Free/Premium)
 * - Notification bell with count (placeholder)
 * - Profile button with completion indicator (Phase 4.8)
 * - Logout button
 * - Responsive design (stacks on mobile)
 * 
 * Created: 2025-01-16 (Dashboard Redesign Phase 1)
 * Updated: 2025-01-17 (Phase 4.8: Added Profile button)
 * Phase: Dashboard Redesign - Phase 1: Foundation + Phase 4.8
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTier } from '../../contexts/TierContext';
import { profileAPI } from '../../services/api';
import logo from '../../assets/logo.png';

const DashboardHeader = () => {
  const navigate = useNavigate();
  const { user, logout } = useTier();
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);

  // Check profile completion status and photo
  useEffect(() => {
    const checkProfileCompletion = async () => {
      try {
        const response = await profileAPI.getProfile();
        setProfileIncomplete(!response.data.profile_completed);
        setProfilePhotoUrl(response.data.profile_photo_url);
      } catch (err) {
        console.error('Failed to check profile completion:', err);
      }
    };

    if (user) {
      checkProfileCompletion();
    }
  }, [user]);

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return 'U';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) return firstName.substring(0, 2).toUpperCase();
    if (lastName) return lastName.substring(0, 2).toUpperCase();
    return 'U';
  };
  
  const getFullName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.email;
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'premium':
        return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white';
      case 'free':
        return 'bg-gray-200 text-gray-700';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
      <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        
        {/* Left Section: Logo and Title */}
        <div className="flex items-center space-x-4">
          <img 
            src={logo} 
            alt="BizHub Logo" 
            className="h-10 w-auto"
          />
          <div className="hidden sm:block">
            <h1 className="text-xl font-bold text-gray-900">Tax Dashboard</h1>
            <p className="text-xs text-gray-500">Let's Talk Strategy</p>
          </div>
        </div>

        {/* Right Section: Notifications, Profile, User Info, Logout */}
        <div className="flex items-center space-x-4">
          
          {/* Notification Bell (Placeholder) */}
          <button 
            className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Notifications"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {/* Notification Badge (Placeholder - Future: Dynamic count) */}
            <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
          </button>

          {/* Profile Button (Phase 4.8) */}
          <button 
            onClick={() => navigate('/profile')}
            className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Profile"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {/* Profile Incomplete Indicator */}
            {profileIncomplete && (
              <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-yellow-500 ring-2 ring-white"></span>
            )}
          </button>

          {/* User Info */}
          {user && (
            <div className="flex items-center space-x-3">
              
              {/* User Avatar */}
              <div className="hidden md:flex items-center space-x-3">
                <div className="flex flex-col items-end">
                  <span className="text-sm font-semibold text-gray-900">
                    {getFullName(user)}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getTierBadgeColor(user.tier)}`}>
                    {user.tier ? user.tier.charAt(0).toUpperCase() + user.tier.slice(1) : 'Free'}
                  </span>
                </div>
                
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-teal-500 flex items-center justify-center text-white font-bold overflow-hidden shadow-md">
                  {profilePhotoUrl ? (
                    <img src={profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    getInitials(user.first_name, user.last_name)
                  )}
                </div>
              </div>

              {/* Mobile Avatar (No Name) */}
              <div className="md:hidden h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-teal-500 flex items-center justify-center text-white font-bold overflow-hidden shadow-md">
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  getInitials(user.first_name, user.last_name)
                )}
              </div>

            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={logout}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Logout"
          >
            <span className="hidden sm:inline">Logout</span>
            <svg className="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>

        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
